import { EventTimingStatus } from '../../../enums/event.enum'
import { DayWeekShort, EventDay, EventSlot, EventTimings, UpdateDayTimingsPayload } from '../../../types/event.type'

/**
 * @description Canonical weekday short labels used when normalizing API `day_week` values.
 */
const WEEKDAY_CANON: readonly DayWeekShort[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * @description Parses API `public_start_date` / `public_end_date` (ISO-8601 or space-separated) into date and time parts for UI and payloads.
 * @param value Raw datetime string from the API or empty.
 * @return Object with `date` (`YYYY-MM-DD`) and `time` (`HH:mm`).
 */
export function parsePublicDateTimeFromApi(value: string): { date: string; time: string } {
  const v = (value ?? '').trim()
  if (!v) return { date: '', time: '00:00' }

  if (/^\d{4}-\d{2}-\d{2}T/.test(v) || (v.includes('T') && !v.includes(' '))) {
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return { date: '', time: '00:00' }
    const y = d.getUTCFullYear()
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
    const da = String(d.getUTCDate()).padStart(2, '0')
    const h = String(d.getUTCHours()).padStart(2, '0')
    const m = String(d.getUTCMinutes()).padStart(2, '0')
    return { date: `${y}-${mo}-${da}`, time: `${h}:${m}` }
  }

  const sp = v.indexOf(' ')
  if (sp > 0) {
    const datePart = v.slice(0, 10)
    const timePart = v
      .slice(sp + 1)
      .trim()
      .slice(0, 8)
    const hm = timePart.length >= 5 ? timePart.slice(0, 5) : '00:00'
    return { date: datePart, time: hm }
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return { date: v, time: '00:00' }
  }

  return { date: '', time: '00:00' }
}

/**
 * @description Converts an API public datetime into epoch milliseconds using the event's UTC offset.
 * Falls back to the browser's timezone when the offset is missing or unrecognized.
 * @param value Raw `public_start_date` / `public_end_date` from the API.
 * @param offset Event `timezone_offset_string` (e.g. `+3`, `-5`, `+05:30`).
 * @return Epoch milliseconds, or `NaN` when the value cannot be parsed.
 */
export function parsePublicDateTimeToEpoch(value: string, offset?: string | number | null): number {
  const { date, time } = parsePublicDateTimeFromApi(value)
  if (!date) return NaN

  const raw = offset == null ? '' : String(offset).trim()
  const hourOnly = raw.match(/^([+-])(\d{1,2})$/)
  const normalizedOffset = /^[+-]\d{2}:\d{2}$/.test(raw) ? raw : hourOnly ? `${hourOnly[1]}${hourOnly[2].padStart(2, '0')}:00` : ''

  return Date.parse(`${date}T${time}:00${normalizedOffset}`)
}

/**
 * @description Builds `UpdateEventTimingsPayload.public_*` datetime string in `YYYY-MM-DD HH:mm` form.
 * @param dateYmd Event date `YYYY-MM-DD`.
 * @param timeHm Time `HH:mm`.
 * @return Combined string for PATCH payloads.
 */
export function formatPublicDateTimeForTimingsPayload(dateYmd: string, timeHm: string): string {
  const d = (dateYmd || '').slice(0, 10)
  const t = (timeHm || '00:00').slice(0, 5)
  return `${d} ${t}`
}

/**
 * @description Parses public start and end from the API into a single range object.
 * @param publicStart API `public_start_date` string.
 * @param publicEnd API `public_end_date` string.
 * @return Combined range or `null` when start date is missing.
 */
export function parsePublicRangeFromApi(publicStart: string, publicEnd: string): { startDate: string; endDate: string; startTime: string; endTime: string } | null {
  const a = parsePublicDateTimeFromApi(publicStart)
  const b = parsePublicDateTimeFromApi(publicEnd)
  if (!a.date) return null
  return {
    startDate: a.date,
    endDate: b.date,
    startTime: a.time,
    endTime: b.time
  }
}

/**
 * @description Minimum slot length in hours for finite scheduler and add-slot flows.
 */
export const MIN_SLOT_DURATION_H = 1

/**
 * @description Start time token allowed for the end-of-day edit-slot exception row.
 */
export const END_OF_DAY_EDIT_SLOT_FROM = '23:00'

/**
 * @description End time token allowed for the end-of-day edit-slot exception row.
 */
export const END_OF_DAY_EDIT_SLOT_TO = '23:59'

/**
 * @description Parses `HH:mm` (or longer string starting with it) to fractional hours.
 * @param t Time string.
 * @return Hours as a decimal number.
 */
export function parseTimeToHours(t: string): number {
  const [h, m] = (t || '00:00').slice(0, 5).split(':').map(Number)
  return (h ?? 0) + (m ?? 0) / 60
}

/**
 * @description Rounds fractional hours to the nearest 30 minutes and formats as `HH:mm`.
 * @param hours Decimal hours.
 * @return Formatted `HH:mm` string.
 */
export function formatDecimalToTime(hours: number): string {
  const snapped = Math.round(hours * 2) / 2
  const h = Math.floor(snapped)
  const m = snapped - h >= 0.5 ? 30 : 0
  return `${String(h).padStart(2, '0')}:${m === 30 ? '30' : '00'}`
}

/**
 * @description Merges overlapping or adjacent slots on a 30-minute grid; drops invalid ranges.
 * @param slots Raw slot list.
 * @return Merged slot list sorted by start time.
 */
export function mergeTimeSlots(slots: EventSlot[]): EventSlot[] {
  if (slots.length === 0) return []
  const normalized = slots
    .map((s) => ({
      startH: parseTimeToHours(s.from),
      endH: parseTimeToHours(s.to),
      label: (s.label ?? '').trim()
    }))
    .filter((x) => x.endH > x.startH)
    .sort((a, b) => a.startH - b.startH)
  const merged: { startH: number; endH: number; label: string }[] = []
  for (const cur of normalized) {
    if (merged.length === 0) {
      merged.push({ ...cur })
      continue
    }
    const prev = merged[merged.length - 1]
    if (cur.startH <= prev.endH) {
      prev.endH = Math.max(prev.endH, cur.endH)
      if (cur.label) {
        prev.label = prev.label ? (prev.label.includes(cur.label) ? prev.label : `${prev.label} ${cur.label}`.trim()) : cur.label
      }
    } else {
      merged.push({ ...cur })
    }
  }
  return merged.map((m) => ({
    from: formatDecimalToTime(m.startH),
    to: formatDecimalToTime(m.endH),
    label: m.label
  }))
}

/**
 * @description Stable sort for slot arrays by from, to, then label.
 * @param slots Slot array to copy and sort.
 * @return New sorted array.
 */
export function sortSlotsByTime(slots: EventSlot[]): EventSlot[] {
  return [...slots].sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to) || (a.label ?? '').localeCompare(b.label ?? ''))
}

/**
 * @description Formats an IANA timezone as `Timezone (City, GMT +N)` with a compact offset label when possible.
 * @param timezone IANA zone name or empty.
 * @return Human-readable label string.
 */
export function formatTimezoneLabel(timezone: string | null | undefined): string {
  const tz = (timezone || 'UTC').trim()
  if (!tz) return 'Timezone (UTC)'
  const city =
    tz
      .split('/')
      .pop()
      ?.replace(/_/g, ' ')
      ?.replace(/\b\w/g, (c) => c.toUpperCase()) ?? tz
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      timeZoneName: 'longOffset'
    }).formatToParts(new Date())
    const tzPart = parts.find((p) => p.type === 'timeZoneName')
    const raw = tzPart?.value ?? 'GMT'
    const match = raw.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
    const offsetLabel = match ? `GMT ${match[1]}${parseInt(match[2], 10)}` : 'GMT'
    return `Timezone (${city}, ${offsetLabel})`
  } catch {
    return `Timezone (${city})`
  }
}

/**
 * @description Normalizes one API timing row into local `EventDay` shape with sorted slots.
 * @param d API timing row.
 * @return Local `EventDay` object.
 */
export function normalizeApiTimingDay(d: EventTimings): EventDay {
  return {
    id: String(d.id ?? ''),
    start_date: d.start_date,
    end_date: d.end_date,
    info: d.info,
    state: d.state,
    slots: sortSlotsByTime(
      (d.slots ?? []).map((s) => ({
        from: s.from,
        to: s.to,
        label: s.label ?? ''
      }))
    )
  }
}

/**
 * @description Maps API timing rows to `EventDay[]`.
 * @param rows API rows.
 * @return Normalized day list.
 */
export function eventTimingsRowsToDays(rows: EventTimings[]): EventDay[] {
  return rows.map(normalizeApiTimingDay)
}

/**
 * @description Builds a stable signature string for finite timings so identical API payloads are recognized across object reference changes.
 * @param eventId Event identifier.
 * @param rows API timing rows.
 * @return String key suitable for comparison.
 */
export function finiteTimingsHydrationKey(eventId: number, rows: EventTimings[]): string {
  const payload = rows.map((r) => ({
    id: r.id ?? '',
    start_date: r.start_date ?? '',
    end_date: r.end_date ?? '',
    state: r.state,
    day_week: r.day_week ?? '',
    slots: [...(r.slots ?? [])]
      .map((s) => ({
        from: s.from ?? '',
        to: s.to ?? '',
        label: (s.label ?? '').trim()
      }))
      .sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to) || a.label.localeCompare(b.label))
  }))
  return `${eventId}\n${JSON.stringify(payload)}`
}

/**
 * @description Maps API `day_week` variants onto `DayWeekShort`.
 * @param raw Raw API value.
 * @return Canonical weekday or `null`.
 */
export function normalizeDayWeekShort(raw: unknown): DayWeekShort | null {
  if (raw == null) return null
  const s = String(raw).trim()
  if (!s) return null
  if ((WEEKDAY_CANON as readonly string[]).includes(s)) return s as DayWeekShort
  const k = s.replace(/\.$/, '').toLowerCase()
  const map: Record<string, DayWeekShort> = {
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sun: 'Sun',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat'
  }
  return map[k] ?? null
}

/**
 * @description Two-digit minute `00`–`59` from API input with non-digits stripped.
 * @param minute Raw minute token.
 * @return Two-digit minute string.
 */
export function clampMinuteToken(minute: string | null | undefined): string {
  const n = Number.parseInt(
    String(minute ?? '0')
      .replace(/\D/g, '')
      .slice(0, 2),
    10
  )
  const m = Number.isFinite(n) ? Math.min(59, Math.max(0, n)) : 0
  return String(m).padStart(2, '0')
}

/**
 * @description Pads hour to two digits and clamps between 0 and 23.
 * @param h Raw hour token.
 * @return Two-digit hour string.
 */
export function padHourPart(h: string): string {
  const n = Number.parseInt(String(h ?? '0'), 10)
  return Number.isFinite(n) ? String(Math.min(23, Math.max(0, n))).padStart(2, '0') : '09'
}

/**
 * @description Extracts wall-clock hour and minute from API `from` / `to` strings without matching time inside the date portion.
 * @param raw Raw slot field value.
 * @param fallbackH Fallback hour when empty.
 * @param fallbackM Fallback minute when empty.
 * @return Object with `h` and `m` two-digit strings.
 */
export function extractHmFromSlotField(raw: string | null | undefined, fallbackH: string, fallbackM: string): { h: string; m: string } {
  const v = (raw ?? '').trim()
  if (!v) {
    return { h: padHourPart(fallbackH), m: clampMinuteToken(fallbackM) }
  }
  const iso = v.match(/T(\d{1,2}):(\d{2})(?::\d{2})?/)
  if (iso) {
    return { h: padHourPart(iso[1]), m: clampMinuteToken(iso[2]) }
  }
  const afterSpace = v.match(/\s(\d{1,2}):(\d{2})(?::\d{2})?/)
  if (afterSpace) {
    return { h: padHourPart(afterSpace[1]), m: clampMinuteToken(afterSpace[2]) }
  }
  const plain = v.match(/^(\d{1,2}):(\d{2})/)
  if (plain) {
    return { h: padHourPart(plain[1]), m: clampMinuteToken(plain[2]) }
  }
  return { h: padHourPart(fallbackH), m: clampMinuteToken(fallbackM) }
}

/**
 * @description For ISO `from`/`to`, interprets clock in `eventTimezone` (IANA); plain `HH:mm` skips `Date` parsing.
 * @param raw Raw slot field value.
 * @param fallbackH Fallback hour when empty.
 * @param fallbackM Fallback minute when empty.
 * @param eventTimezone IANA timezone or empty.
 * @return Object with `h` and `m` two-digit strings.
 */
export function wallClockHmFromSlotField(raw: string | null | undefined, fallbackH: string, fallbackM: string, eventTimezone: string | null | undefined): { h: string; m: string } {
  const v = (raw ?? '').trim()
  if (!v) {
    return { h: padHourPart(fallbackH), m: clampMinuteToken(fallbackM) }
  }
  const tz = (eventTimezone ?? '').trim()
  if (tz && (v.includes('T') || /Z|[+-]\d{2}:?\d{2}$/.test(v))) {
    const d = new Date(v)
    if (!Number.isNaN(d.getTime())) {
      try {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: tz,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }).formatToParts(d)
        const hh = parts.find((p) => p.type === 'hour')?.value
        const mm = parts.find((p) => p.type === 'minute')?.value
        if (hh != null && mm != null) {
          return { h: padHourPart(hh), m: clampMinuteToken(mm) }
        }
      } catch {}
    }
  }
  return extractHmFromSlotField(v, fallbackH, fallbackM)
}

/**
 * @description Returns the first slot when indefinite UI uses `slots[0]` for opening and closing.
 * @param slots Slot list or empty.
 * @return First non-empty slot or `null`.
 */
export function firstTimingSlot(slots: EventSlot[] | null | undefined): EventSlot | null {
  const s = slots?.[0]
  if (!s) return null
  const from = (s.from ?? '').trim()
  const to = (s.to ?? '').trim()
  if (!from && !to) return null
  return s
}

/**
 * @description Builds a stable key for comparing slot wall-clock ranges across days.
 * @param slot Slot to read.
 * @param eventTimezone Optional IANA zone for wall-clock extraction.
 * @return Key string `H:M|H:M`.
 */
export function slotTimeRangeKey(slot: EventSlot, eventTimezone?: string | null): string {
  const o = wallClockHmFromSlotField(slot.from ?? '', '0', '0', eventTimezone ?? null)
  const c = wallClockHmFromSlotField(slot.to ?? '', '0', '0', eventTimezone ?? null)
  return `${o.h}:${o.m}|${c.h}:${c.m}`
}

/**
 * @description Derives whether indefinite weekly hours are the same every day or differ by weekday.
 * @param weekRows API rows for the week.
 * @param eventTimezone Optional IANA zone.
 * @return `'same'` or `'different'`.
 */
export function indefiniteOpeningHoursModeFromWeekRows(weekRows: EventTimings[], eventTimezone?: string | null): 'same' | 'different' {
  if (weekRows.length === 0) return 'same'
  for (const r of weekRows) {
    const n = (r.slots ?? []).length
    if (n > 1) return 'different'
    if (r.state !== EventTimingStatus.INACTIVE && n !== 1) return 'different'
  }
  const withSlot = weekRows.filter((r) => (r.slots ?? []).length === 1)
  if (withSlot.length === 0) return 'same'
  const ref = slotTimeRangeKey(withSlot[0].slots![0], eventTimezone)
  return withSlot.every((r) => slotTimeRangeKey(r.slots![0], eventTimezone) === ref) ? 'same' : 'different'
}

/**
 * @description Reads per-day open and close from the first slot using event timezone when needed.
 * @param slots Slot list or empty.
 * @param eventTimezone Optional IANA zone.
 * @return Hour and minute parts or `null` when unusable.
 */
export function perDayHoursFromFirstSlot(
  slots: EventSlot[] | null | undefined,
  eventTimezone?: string | null
): {
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
} | null {
  const slot = firstTimingSlot(slots)
  if (!slot) return null
  const open = wallClockHmFromSlotField(slot.from, '9', '0', eventTimezone ?? null)
  const close = wallClockHmFromSlotField(slot.to, '17', '0', eventTimezone ?? null)
  const startDec = parseTimeToHours(`${open.h}:${open.m}`)
  const endDec = parseTimeToHours(`${close.h}:${close.m}`)
  if (!(endDec > startDec)) {
    return {
      startHour: '09',
      startMinute: '00',
      endHour: '17',
      endMinute: '00'
    }
  }
  return {
    startHour: open.h,
    startMinute: open.m,
    endHour: close.h,
    endMinute: close.m
  }
}

/**
 * @description Snaps minute to indefinite weekly grid values `00`, `15`, `30`, `45`, or `59`.
 * @param m Raw minute token.
 * @return Snapped two-digit minute string.
 */
export function snapMinuteToIndefiniteWeekly(m: string): string {
  const n = Number.parseInt(
    String(m ?? '0')
      .replace(/\D/g, '')
      .slice(0, 2),
    10
  )
  const v = Number.isFinite(n) ? Math.min(59, Math.max(0, n)) : 0
  if (v >= 52) return '59'
  if (v >= 37) return '45'
  if (v >= 22) return '30'
  if (v >= 7) return '15'
  return '00'
}

/**
 * @description Adds wall-clock minutes, capped at `23:59` the same day.
 * @param h Start hour string.
 * @param m Start minute string.
 * @param deltaMin Minutes to add (may be negative).
 * @return New hour and minute strings.
 */
export function addWallClockMinutes(h: string, m: string, deltaMin: number): { h: string; m: string } {
  const base = Math.round(parseTimeToHours(`${h}:${m}`) * 60) + deltaMin
  const capped = Math.min(23 * 60 + 59, Math.max(0, base))
  const hh = Math.floor(capped / 60)
  const mm = capped % 60
  return { h: padHourPart(String(hh)), m: String(mm).padStart(2, '0') }
}

/**
 * @description Snaps open and close to the 15-minute grid and ensures closing is after opening.
 * @param t Wall-clock parts for start and end.
 * @return Coerced hour and minute strings.
 */
export function coerceIndefiniteWeeklyRange(t: { startHour: string; startMinute: string; endHour: string; endMinute: string }): {
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
} {
  const startMinute = snapMinuteToIndefiniteWeekly(t.startMinute)
  let endMinute = snapMinuteToIndefiniteWeekly(t.endMinute)
  let endHour = t.endHour
  const startH = parseTimeToHours(`${t.startHour}:${startMinute}`)
  let endH = parseTimeToHours(`${endHour}:${endMinute}`)
  if (endH > startH) {
    return { startHour: t.startHour, startMinute, endHour, endMinute }
  }
  const bumped15 = addWallClockMinutes(t.startHour, startMinute, 15)
  endHour = bumped15.h
  endMinute = snapMinuteToIndefiniteWeekly(bumped15.m)
  endH = parseTimeToHours(`${endHour}:${endMinute}`)
  if (endH > startH) {
    return { startHour: t.startHour, startMinute, endHour, endMinute }
  }
  const bumped30 = addWallClockMinutes(t.startHour, startMinute, 30)
  endHour = bumped30.h
  endMinute = snapMinuteToIndefiniteWeekly(bumped30.m)
  endH = parseTimeToHours(`${endHour}:${endMinute}`)
  if (endH > startH) {
    return { startHour: t.startHour, startMinute, endHour, endMinute }
  }
  const bumped60 = addWallClockMinutes(t.startHour, startMinute, 60)
  endHour = bumped60.h
  endMinute = snapMinuteToIndefiniteWeekly(bumped60.m)
  endH = parseTimeToHours(`${endHour}:${endMinute}`)
  if (endH > startH) {
    return { startHour: t.startHour, startMinute, endHour, endMinute }
  }
  return { startHour: t.startHour, startMinute, endHour: '23', endMinute: '59' }
}

/**
 * @description Resolves JSON body `id` for PATCH payloads (numeric id versus UUID string) from the path timing id.
 * @param pathTimingId Timing id from the route or row.
 * @return Numeric id or original string id.
 */
export function timingRowIdForPayload(pathTimingId: string): number | string {
  const s = String(pathTimingId).trim()
  if (s === '') return 0
  if (/^\d+$/.test(s)) return Number(s)
  return s
}

/**
 * @description Builds PATCH payload for a single finite day row.
 * @param day Local day row.
 * @param state Active or inactive UI state.
 * @param info Optional highlight note string.
 * @param slots Slot list to persist.
 * @param pathTimingId Timing id for payload `id` field.
 * @return `UpdateDayTimingsPayload` object.
 */
export function buildUpdateDayPayload(day: EventDay, state: 'active' | 'inactive', info: string | null, slots: EventSlot[], pathTimingId: string): UpdateDayTimingsPayload {
  const sorted = [...slots].sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to))
  return {
    id: timingRowIdForPayload(pathTimingId),
    start_date: day.start_date,
    end_date: day.end_date,
    info: info ?? '',
    state: state === 'active' ? EventTimingStatus.ACTIVE : EventTimingStatus.INACTIVE,
    slots: sorted.map((s) => ({
      from: s.from,
      to: s.to === '24:00' || (s.to ?? '').startsWith('24:') ? '23:59' : s.to,
      label: s.label ?? ''
    }))
  }
}
